import { ThHTMLAttributes, ReactNode } from 'react';
import type { SortState } from '../../lib/useSortableTable';
import { useTooltip } from './Tooltip';
import { GLOSSARY, type GlossaryKey } from '../../lib/glossary';

export interface SortableThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  columnKey: string;
  sort: SortState | null;
  onToggle: (key: string) => void;
  children: ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Glossary key or inline text explained on hover/focus of the header label. */
  help?: GlossaryKey | string;
}

const ALIGN_CLASSES: Record<NonNullable<SortableThProps['align']>, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

export default function SortableTh({
  columnKey,
  sort,
  onToggle,
  children,
  align = 'left',
  className = '',
  help,
  ...rest
}: SortableThProps) {
  const active = sort?.key === columnKey;
  const dir = active ? sort?.dir : undefined;
  const ariaSort: 'ascending' | 'descending' | 'none' =
    dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none';
  const indicator = dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : '↕';
  const helpText = help ? (help in GLOSSARY ? GLOSSARY[help as GlossaryKey].def : help) : undefined;
  const { ref, triggerProps, tooltip } = useTooltip<HTMLButtonElement>(helpText);

  return (
    <th
      {...rest}
      scope="col"
      aria-sort={ariaSort}
      className={`whitespace-nowrap px-2.5 pb-2.5 pt-0 font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-muted ${ALIGN_CLASSES[align]} ${className}`}>
      <button
        ref={ref}
        type="button"
        onClick={() => onToggle(columnKey)}
        {...(helpText ? triggerProps : {})}
        className="inline-flex items-center gap-1 bg-transparent p-0 font-semibold uppercase text-inherit hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <span className={helpText ? 'border-b border-dotted border-muted' : ''}>{children}</span>
        <span aria-hidden="true" className={active ? 'text-ink' : 'text-muted/50'}>
          {indicator}
        </span>
      </button>
      {tooltip}
    </th>
  );
}
